import { API_BASE_URL } from '../../../config';
import type {
  AuthTokens,
  LoginCredentials,
  RegisterData,
  MfaVerifyData,
  MfaSetupResponse,
  PasswordResetRequestData,
  PasswordResetData,
  User,
  OAuthProviderId,
  ApiErrorResponse,
} from '../types';

const API_BASE = `${API_BASE_URL}/api/v1/auth`;

class AuthApiError extends Error {
  statusCode: number;
  errors?: { field: string; message: string }[];

  constructor(response: ApiErrorResponse) {
    super(response.message);
    this.name = 'AuthApiError';
    this.statusCode = response.statusCode;
    this.errors = response.errors;
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData: ApiErrorResponse = await response.json().catch(() => ({
      statusCode: response.status,
      message: response.statusText || 'Request failed',
    }));
    throw new AuthApiError(errorData);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

function getAuthHeaders(accessToken?: string): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  return headers;
}

export const authApi = {
  async login(credentials: LoginCredentials): Promise<AuthTokens & { user?: User; mfaRequired?: boolean; sessionToken?: string }> {
    const response = await fetch(`${API_BASE}/login`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(credentials),
    });
    return handleResponse(response);
  },

  async register(data: RegisterData): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE}/register`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        fullName: data.fullName,
        email: data.email,
        password: data.password,
        referralCode: data.referralCode,
      }),
    });
    return handleResponse(response);
  },

  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    const response = await fetch(`${API_BASE}/refresh`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ refreshToken }),
    });
    return handleResponse(response);
  },

  async logout(accessToken: string, refreshToken: string): Promise<void> {
    const response = await fetch(`${API_BASE}/logout`, {
      method: 'POST',
      headers: getAuthHeaders(accessToken),
      body: JSON.stringify({ refreshToken }),
    });
    return handleResponse(response);
  },

  async oauthLogin(provider: OAuthProviderId, code: string, codeVerifier: string): Promise<AuthTokens & { user: User }> {
    const response = await fetch(`${API_BASE}/oauth/${provider}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ code, codeVerifier }),
    });
    return handleResponse(response);
  },

  async requestPasswordReset(data: PasswordResetRequestData): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE}/password/forgot`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async resetPassword(data: PasswordResetData): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE}/password/reset`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        token: data.token,
        password: data.password,
      }),
    });
    return handleResponse(response);
  },

  async verifyEmail(token: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE}/email/verify`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ token }),
    });
    return handleResponse(response);
  },

  async resendVerificationEmail(email: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE}/email/resend`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ email }),
    });
    return handleResponse(response);
  },

  async enableMfa(accessToken: string, password: string): Promise<MfaSetupResponse> {
    const response = await fetch(`${API_BASE}/mfa/enable`, {
      method: 'POST',
      headers: getAuthHeaders(accessToken),
      body: JSON.stringify({ password }),
    });
    return handleResponse(response);
  },

  async disableMfa(accessToken: string, password: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE}/mfa/disable`, {
      method: 'POST',
      headers: getAuthHeaders(accessToken),
      body: JSON.stringify({ password }),
    });
    return handleResponse(response);
  },

  async verifyMfa(data: MfaVerifyData): Promise<AuthTokens & { user: User }> {
    const response = await fetch(`${API_BASE}/mfa/verify`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async getMe(accessToken: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/api/v1/users/me`, {
      method: 'GET',
      headers: getAuthHeaders(accessToken),
    });
    return handleResponse(response);
  },
};

export { AuthApiError };
export default authApi;
